/* eslint-disable modernloop/restrict-imports.cjs */
import React from 'react';

import { logError } from '@modernloop/shared/utils';

import HelpCenterUrl from 'src/constants/HelpCenterUrl';

import AshbyCreateCandidatePermissionDialog from 'src/views-new/JobDetailsPage/SourcingLinks/AtsCreateCandidatePermissionDialogs/AshbyCreateCandidatePermissionDialog';
import AshbyCard, { Props as AshbyCardProps } from 'src/views-new/Settings/Integrations/Cards/AshbyCard';

import { ListIntegrationResponse } from '../api/integration/useIntegrations';

import AtsSevice, { ExtensionSidebarParsedUrlType, ParsedAtsUrlResult, ScorecardGroupByStrategy } from './AtsService';

/* *
 * Ashby ATS service
 * Imp: Never have atsType set in ATS service
 * */
class AshbyAtsService implements AtsSevice {
  readonly featureFlag = null;

  readonly name = 'Ashby';

  readonly hostname = 'ashby'; // ex. https://app.ashbyhq.com/

  readonly logoUrl = `/static/images/integrations/ashby.png`;

  readonly helpCenterUrl = HelpCenterUrl.ASHBY_INTEGRATION;

  readonly scorecardLabelPluralTranslationKey = 'hiring-dashboard-common:common.ats.interviewTypes';

  readonly noScorecardLabelTranslationKey = 'hiring-dashboard-common:common.ats.noInterviewType';

  readonly scorecardSelectTitleTranslationKey = 'hiring-dashboard-common:common.ats.selectInterviewType';

  readonly scorecardSearchPlaceholderTranslationKey = 'hiring-dashboard-common:common.ats.searchInterviewTypes';

  readonly scorecardSearchPlaceholderEllipsisTranslationKey =
    'hiring-dashboard-common:common.ats.searchInterviewTypesEllipsis';

  readonly isSourcingLinkEnabled = true;

  readonly isAtsUserSelectEnabled = false;

  readonly isAtsImportAvailabilityEnabled = false;

  readonly skipInterviewKitLinkTokenValidation = true;

  readonly scorecardGroupByKey = null;

  readonly scorecardGroupByStrategy = ScorecardGroupByStrategy.AshbyInterviewScope;

  readonly scorecardSortKey = null;

  readonly showEstimatedMinutes = false;

  readonly createCandidatePermissionDialog = AshbyCreateCandidatePermissionDialog;

  readonly showRequisitionId = false;

  readonly canSupportBrightHire = false;

  readonly hasScorecardSync = true;

  readonly scorecardSyncSettingLabelTranslationKey =
    'hiring-dashboard-common:common.ats.keepInterviewsInSyncWithAshbyInterviewPlan';

  readonly hasHiringProcess = false;

  readonly hasScorecards = true;

  readonly hasEnforcedJobPermissions = false;

  readonly resetInterviewPlanWarningMessage =
    "The interview plan for this stage will be reset to its current settings in Ashby, removing all ModernLoop configurations. Interviewer selections can't be imported at this time.";

  // eslint-disable-next-line class-methods-use-this
  getIntegrationCard = (integrationCardProps: AshbyCardProps, integrations: ListIntegrationResponse): JSX.Element => {
    return <AshbyCard {...integrationCardProps} ashby={integrations?.ashby} />;
  };

  parseExtensionSidebarUrl = (externalUrl: string): ParsedAtsUrlResult | null => {
    try {
      const url: URL | undefined = new URL(externalUrl);

      // check if url is ashby
      if (!url.hostname.includes(this.hostname)) {
        return null; // exit early
      }

      // check if url has candidates tab open
      if (url.pathname.includes('/right-side/candidates')) {
        // "candidates" shows up after the righ-side keyword
        // "applications" shows up after the candidates keyword
        // their respective ids are the next item in the path
        // ex. https://app.ashbyhq.com/candidates/pipeline/active/right-side/candidates/037871ef-5c80-45c3-99df-f1d57fe505b3/applications/ac09fb12-984c-42a0-a1ca-31191a40967e/feed
        // ex. https://app.ashbyhq.com/candidates/right-side/candidates/037871ef-5c80-45c3-99df-f1d57fe505b3/applications/ac09fb12-984c-42a0-a1ca-31191a40967e/feed
        const folders = url.pathname.split('/') || [];
        const rightSideKeywordIndex = folders.indexOf('right-side');
        const candidatesKeywordIndex = folders.indexOf('candidates', rightSideKeywordIndex);
        const applicationsKeywordIndex = folders.indexOf('applications', candidatesKeywordIndex);
        if (rightSideKeywordIndex > -1 && candidatesKeywordIndex > -1 && applicationsKeywordIndex > -1) {
          const ashbyCandidateId = folders[candidatesKeywordIndex + 1];
          const ashbyApplicationId = folders[applicationsKeywordIndex + 1];
          if (ashbyCandidateId && ashbyApplicationId) {
            return {
              type: ExtensionSidebarParsedUrlType.Candidate,
              atsCandidateId: ashbyCandidateId,
              atsApplicationId: ashbyApplicationId,
            };
          }
        }
      }
    } catch (e) {
      logError('Error parsing Ashby URL', e);
    }
    return null;
  };
}

export default new AshbyAtsService();
